/** Shared helpers for the Site Builder pages (user console). */
import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { Button, Modal, Tag, Tooltip, Typography, message, theme } from 'antd';
import { CopyOutlined, QuestionCircleOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import { t as tt } from '../../../shared/i18n';
import { formatMoney } from '../../../shared/utils/money';
import { userPath } from '../../../shared/config/paths';

const { Text } = Typography;

export const SITE_SUFFIX = 'sites.yeapcloud.net';
export const sitePath = (id, sub = '') => userPath(`/sites/${id}${sub ? (sub.startsWith('/') ? sub : `/${sub}`) : ''}`);
export const editorPath = (id, pageId) => userPath(`/sites/${id}/editor${pageId ? `/${pageId}` : ''}`);

/** apiClient toasts most HTTP errors itself; only show the ones it did not */
export const toastErr = (e, fallback) => {
    if (!e?.toasted) message.error(e?.error || e?.message || fallback || tt('Operation failed'));
};

/** list from [..] | {items} | {data} | {list} | {rows} */
export const listOf = (d) => {
    if (Array.isArray(d)) return d;
    for (const k of ['items', 'data', 'list', 'rows', 'results']) if (Array.isArray(d?.[k])) return d[k];
    return [];
};
/** total of a paged response */
export const totalOf = (d) => Number(d?.total ?? d?.pagination?.total ?? d?.meta?.total ?? listOf(d).length) || 0;

export const fmtDate = (v, withTime = true) => {
    if (!v) return '-';
    const d = new Date(String(v).replace(' ', 'T') + (/[zZ]|[+-]\d\d:?\d\d$/.test(String(v)) || String(v).includes('T') ? '' : 'Z'));
    if (Number.isNaN(d.getTime())) return String(v);
    return dayjs(d).format(withTime ? 'YYYY-MM-DD HH:mm' : 'YYYY-MM-DD'); // same format as the rest of the console
};

// ---- money: the shop stores integer minor units (cents) in the shop's currency ----
const ZERO_DECIMAL = new Set(['JPY', 'KRW', 'TWD', 'VND', 'CLP', 'ISK', 'HUF', 'UGX', 'XAF', 'XOF']);
export const currencyDigits = (c) => (ZERO_DECIMAL.has(String(c || '').toUpperCase()) ? 0 : 2);
export const fromMinor = (v, c) => (v === null || v === undefined || v === '' ? null : Number(v) / 10 ** currencyDigits(c));
export const toMinor = (v, c) => (v === null || v === undefined || v === '' ? null : Math.round(Number(v) * 10 ** currencyDigits(c)));
/** Shop money (minor units) → "$12.50"; never converted to the viewer's display currency (it is a record). */
export const money = (minor, currency, opts = {}) => formatMoney(fromMinor(minor, currency), currency, { original: true, decimals: currencyDigits(currency), ...opts });

export const CURRENCIES = ['USD', 'EUR', 'GBP', 'CNY', 'JPY', 'HKD', 'TWD', 'SGD', 'AUD', 'CAD', 'KRW', 'MYR', 'INR', 'BRL', 'RUB', 'CHF'];

// ---- status tags ----
const COLORS = {
    active: 'green', published: 'green', paid: 'green', fulfilled: 'green', enabled: 'green', succeeded: 'green', verified: 'green', open: 'blue',
    draft: 'default', pending: 'gold', partial: 'orange', partially_refunded: 'orange', unfulfilled: 'gold', archived: 'default',
    suspended: 'red', error: 'red', failed: 'red', cancelled: 'default', refunded: 'purple', closed: 'default', deleted: 'default', disabled: 'default',
    manual: 'blue', smart: 'purple',
};
const LABELS = {
    active: 'Active', published: 'Published', paid: 'Paid', fulfilled: 'Fulfilled', enabled: 'Enabled', succeeded: 'Succeeded', open: 'Open',
    draft: 'Draft', pending: 'Pending', partial: 'Partially fulfilled', partially_refunded: 'Partially refunded', unfulfilled: 'Unfulfilled',
    archived: 'Archived', suspended: 'Suspended', error: 'Error', failed: 'Failed', cancelled: 'Cancelled', refunded: 'Refunded',
    closed: 'Closed', deleted: 'Deleted', disabled: 'Disabled', verified: 'Verified', manual: 'Manual', smart: 'Smart',
};
export const StatusTag = ({ value, style }) => (value ? <Tag color={COLORS[value] || 'default'} style={style}>{tt(LABELS[value] || String(value), { context: 'order' })}</Tag> : null);

export const Hint = ({ title }) => (
    <Tooltip title={title}><QuestionCircleOutlined style={{ color: 'var(--ant-color-text-tertiary, #999)', marginLeft: 4 }} /></Tooltip>
);

export const copyText = async (text) => {
    try {
        await navigator.clipboard.writeText(String(text ?? ''));
        message.success(tt('Copied'));
    } catch (_) {
        message.error(tt('Failed to copy. Please copy manually.'));
    }
};
export const CopyLine = ({ value, style }) => (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, maxWidth: '100%', ...style }}>
        <Text code style={{ wordBreak: 'break-all' }}>{value || '-'}</Text>
        {value ? <Tooltip title={tt('Copy')}><Button size="small" type="text" icon={<CopyOutlined />} onClick={() => copyText(value)} /></Tooltip> : null}
    </span>
);

export const slugify = (s) => String(s || '').toLowerCase().normalize('NFKD').replace(/[̀-ͯ]/g, '')
    .replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '').slice(0, 63);

/** primary URL of a site (custom primary domain → else the free subdomain) */
export const siteUrl = (s) => {
    if (!s) return '';
    if (s.url) return s.url;
    const prim = (s.domains || []).find((d) => d.is_primary || d.primary);
    if (prim?.host) return `https://${prim.host}`;
    return s.slug ? `https://${s.slug}.${SITE_SUFFIX}` : '';
};

// ---- unsaved changes: forms register while dirty; SiteLayout asks before switching sections ----
let unsaved = 0;
export const useLeaveGuard = (dirty) => {
    useEffect(() => {
        if (!dirty) return undefined;
        unsaved += 1;
        return () => { unsaved = Math.max(0, unsaved - 1); };
    }, [dirty]);
};
export const confirmLeave = (go) => {
    if (!unsaved) { go(); return; }
    Modal.confirm({
        title: tt('Discard unsaved changes?'), okText: tt('Discard'), cancelText: tt('Keep editing'), okButtonProps: { danger: true },
        onOk: () => { unsaved = 0; go(); },
    });
};

// ---- theme: the pages style a few boxes with var(--ant-…); antd does not emit CSS variables here, so mirror the tokens
// on <html> while a site page is open (dark mode would otherwise fall back to the light defaults; covers modals too).
const VAR_TOKENS = {
    '--ant-color-bg-container': 'colorBgContainer', '--ant-color-bg-elevated': 'colorBgElevated', '--ant-color-bg-layout': 'colorBgLayout',
    '--ant-color-border': 'colorBorder', '--ant-color-border-secondary': 'colorBorderSecondary', '--ant-color-fill-quaternary': 'colorFillQuaternary',
    '--ant-color-fill-tertiary': 'colorFillTertiary', '--ant-color-info-bg': 'colorInfoBg', '--ant-color-primary-bg': 'colorPrimaryBg',
    '--ant-color-text-tertiary': 'colorTextTertiary',
};
export const useAntVars = () => {
    const { token } = theme.useToken();
    useEffect(() => {
        const st = document.documentElement.style;
        Object.entries(VAR_TOKENS).forEach(([k, v]) => { if (token[v]) st.setProperty(k, token[v]); });
        return () => { Object.keys(VAR_TOKENS).forEach((k) => st.removeProperty(k)); };
    }, [token]);
};

// ---- site context (SiteLayout provides it) ----
export const SiteContext = createContext({ site: null, siteId: null, reload: () => {}, shop: null, reloadShop: () => {} });
export const useSite = () => useContext(SiteContext);

/** Small loader hook: const { data, loading, reload, setData } = useLoad(() => api.get(..), [deps]) */
export const useLoad = (fn, deps = [], { initial = null, onError } = {}) => {
    const [data, setData] = useState(initial);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const seq = useRef(0);
    const reload = useCallback(async () => {
        const my = ++seq.current;
        setLoading(true);
        try {
            const d = await fn();
            if (my === seq.current) { setData(d); setError(null); }
            return d;
        } catch (e) {
            if (my === seq.current) setError(e);
            if (onError) onError(e); else toastErr(e);
            return undefined;
        } finally {
            if (my === seq.current) setLoading(false);
        }
    }, deps); // eslint-disable-line react-hooks/exhaustive-deps
    useEffect(() => { reload(); }, [reload]);
    return { data, loading, error, reload, setData };
};
